import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Target } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { LinkedItemSearchMenu } from 'shared/components/ui/linked-item-search-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { LinkOptions } from 'shared/types/task';

import * as Styled from './task-create-linked-item-select.style';

export type LinkedItemSelectProps = {
  lifeAreaId?: ID | null;
  goalId?: ID | null;
  onChange: (changes: { lifeAreaId: ID | null; goalId: ID | null }) => void;
  onboarding?: boolean;
  goals: Goal[];
  lifeAreas: LifeArea[];
};

export const TaskCreateLinkedItemSelect: React.FC<LinkedItemSelectProps> = ({
  lifeAreaId,
  goalId,
  onChange,
  onboarding,
  goals,
  lifeAreas,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const item = useMemo(
    () =>
      goals.find(({ id }) => id === goalId) ??
      lifeAreas.find(({ id }) => id === lifeAreaId),
    [goals, lifeAreas, goalId, lifeAreaId],
  );

  const onChangeLinkedItem = (link: { id: ID; type: LinkOptions } | null) => {
    onChange({
      lifeAreaId: link?.type === LinkOptions.LifeArea ? link.id : null,
      goalId: link?.type === LinkOptions.Goal ? link.id : null,
    });
    closeMenu();
  };

  return (
    <Styled.Container $onboarding={!!onboarding} ref={containerRef}>
      <Styled.Title onClick={openMenu} $hasValue={!!item}>
        {!onboarding && (
          <Styled.IconContainer>
            <Icon icon={Target} />
          </Styled.IconContainer>
        )}

        <Styled.Label $onboarding={!!onboarding}>
          {item?.name ??
            t(
              onboarding
                ? 'forms.onboarding.linked-item.placeholder'
                : 'forms.new-task.linked-item.placeholder',
            )}
        </Styled.Label>

        <Styled.ChevronContainer>
          <Icon icon={ChevronLeft} />
        </Styled.ChevronContainer>
      </Styled.Title>

      {menuOpen && (
        <LinkedItemSearchMenu
          goals={goals}
          lifeAreas={lifeAreas}
          onChange={onChangeLinkedItem}
          value={item?.id}
          referenceElement={containerRef}
          onOutside={closeMenu}
        />
      )}
    </Styled.Container>
  );
};
